import { useSyncExternalStore } from 'react';
import { isNativePlatform } from '@lib/platform';
import {
  AuthIntent,
  NATIVE_INTENT_TTL_MS,
  clearAuthIntent,
  readAuthIntent,
  setAuthIntent,
} from '@utils/authIntent';

/**
 * A guest's answers to a poll (TECH-197 § Voting "Signed out"). The pick is kept in this tab's
 * memory while the guest decides (it stays ticked, E5), and becomes the replayable `VOTE_POLL`
 * sign-in intent (sessionStorage on web, 30 minutes; the app's own copy on native, 10) ONLY when
 * the guest taps Sign in on B7 (`closePollSignInForSignIn`), as today's auth-prompt flows do: the
 * pick is cast only for the account that signs in from that tap (FE-1d review). Closing B7 without
 * signing in drops that intent (the visible pick stays). A pending intent of any other kind is never
 * replaced: then the pick is not replayed and the guest answers after sign-in.
 *
 * Also the one sign-in drawer for an answer (B7): it is drawn by `PollSignInPromptHost`, at the
 * app's root, because a tap inside the Post sheet closes the sheet before it opens (one drawer at a
 * time, TECH-197 § Where a poll opens).
 */

/** What B7 draws: the poll's place (its avatar) and the picked options' words. */
export interface PollSignInPrompt {
  questionId: string;
  optionTexts: string[];
  place: { name: string; imageUrl: string | null } | null;
}

let prompt: PollSignInPrompt | null = null;
/** Polls whose drawer was turned down in this tab: their card says the pick is saved (E5). */
const dismissed = new Set<string>();
const listeners = new Set<() => void>();

const emit = (): void => {
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Each poll's picks ticked signed out in this tab (shown while B7 decides, E5); never replayed. */
const memoryPicks = new Map<string, string[]>();

/** The `VOTE_POLL` intent's picks for this poll (written by a Sign in tap), none when it holds another. */
const intentPicks = (questionId: string): string[] => {
  const intent = readAuthIntent(isNativePlatform() ? NATIVE_INTENT_TTL_MS : undefined);
  return intent?.kind === AuthIntent.VOTE_POLL && intent.questionId === questionId
    ? intent.optionIds
    : [];
};

/** The picks this tab shows for a poll: what was ticked here, else what a Sign in tap stored. */
export const readPollPicks = (questionId: string): string[] =>
  memoryPicks.get(questionId) ?? intentPicks(questionId);

/**
 * A pending sign-in intent of any other kind (an invite, a join, Going, a live poll's answers, ...): a poll
 * pick never replaces it (FE-L1a review: not only invites and joins), so that pick is not replayed.
 */
const holdsOtherIntent = (): boolean => {
  const kind = readAuthIntent(isNativePlatform() ? NATIVE_INTENT_TTL_MS : undefined)?.kind;
  return kind !== undefined && kind !== AuthIntent.VOTE_POLL;
};

/** Whether a Sign in tap now would replay the pick (B7 promises it counts only then). */
export const pollPickWillReplay = (): boolean => !holdsOtherIntent();

/** This poll's replayable intent goes (nothing else's). */
const dropPollIntent = (questionId: string): void => {
  if (intentPicks(questionId).length > 0) clearAuthIntent();
};

/**
 * Holds the guest's picks in this tab's memory; none left forgets them. A stored intent from an
 * earlier Sign in tap no longer says what is ticked, so it goes (the next Sign in tap writes it).
 */
export const savePollPicks = (questionId: string, optionIds: string[]): void => {
  if (optionIds.length > 0) memoryPicks.set(questionId, optionIds);
  else memoryPicks.delete(questionId);
  dropPollIntent(questionId);
  emit();
};

export const openPollSignIn = (next: PollSignInPrompt): void => {
  prompt = next;
  dismissed.delete(next.questionId);
  emit();
};

/** Closed without signing in: the pick stays ticked (E5), but nothing is left to replay. */
export const dismissPollSignIn = (): void => {
  if (prompt) {
    dismissed.add(prompt.questionId);
    dropPollIntent(prompt.questionId);
  }
  prompt = null;
  emit();
};

/**
 * Sign in was tapped on B7: the pick becomes the `VOTE_POLL` intent (unless an invite or a join is
 * waiting on this sign-in: that one stays and the pick is not replayed), and the drawer goes (the
 * door is a route; this drawer lives at the root). Call before starting sign-in.
 */
export const closePollSignInForSignIn = (): void => {
  if (prompt) {
    const optionIds = readPollPicks(prompt.questionId);
    if (optionIds.length > 0 && !holdsOtherIntent()) {
      setAuthIntent({ kind: AuthIntent.VOTE_POLL, questionId: prompt.questionId, optionIds });
    }
  }
  prompt = null;
  emit();
};

export const usePollSignInPrompt = (): PollSignInPrompt | null =>
  useSyncExternalStore(
    subscribe,
    () => prompt,
    () => null,
  );

/**
 * The guest's picks on one poll while the intent lives, and whether its drawer was turned down in
 * this tab. Read on every change of either (the snapshot is a string, so it compares by value).
 */
export const useSignedOutPicks = (
  questionId: string,
): { optionIds: string[]; dismissed: boolean } => {
  const snapshot = useSyncExternalStore(
    subscribe,
    () => `${dismissed.has(questionId) ? '1' : '0'}|${readPollPicks(questionId).join(',')}`,
    () => '0|',
  );
  const [flag, ids] = snapshot.split('|');
  return { optionIds: ids ? ids.split(',') : [], dismissed: flag === '1' };
};

export const resetPollSignInForTests = (): void => {
  prompt = null;
  memoryPicks.clear();
  dismissed.clear();
  emit();
};
